import { z } from 'zod'
import {
  ChangeId,
  EndDate,
  Id,
  IsActive,
  IsActual,
  NextId,
  ObjectId,
  ObjectUuid,
  OperationTypeId,
  PrevId,
  StartDate,
  UpdateDate,
} from './common-schemas'
import { GarItem } from './gar-item'

export namespace House {
  export interface Data {
    /** @description Unique record identifier. Key field */
    readonly id: number

    /** @description Global unique object identifier (INTEGER) */
    readonly objectId: number

    /** @description Global unique object identifier (GUID) */
    readonly objectGuid: string

    /** @description Identifier of the changing transaction */
    readonly changeId: number

    /** @description Main house number */
    readonly houseNum?: string

    /** @description Additional house number 1 */
    readonly addNum1?: string

    /** @description Additional house number 2 */
    readonly addNum2?: string

    /** @description Main house type */
    readonly houseType?: number

    /** @description Additional house type 1 */
    readonly addType1?: number

    /** @description Additional house type 2 */
    readonly addType2?: number

    /** @description Record action status – reason for record appearance */
    readonly operationTypeId: number

    /** @description Identifier of the record linking to the previous historical record */
    readonly prevId?: number

    /** @description Identifier of the record linking to the subsequent historical record */
    readonly nextId?: number

    /** @description Record entry (update) date */
    readonly updateDate: Date

    /** @description Record validity start */
    readonly startDate: Date

    /** @description Record validity end */
    readonly endDate?: Date

    /** @description Address object relevance indicator */
    readonly isActual: boolean

    /** @description Active address object indicator */
    readonly isActive: boolean
  }
}

/** @description Information about house numbers of streets in cities and settlements */
export class House extends GarItem<'House', House.Data> {
  static readonly SCHEMA: GarItem.Schema<House.Data> = z
    .strictObject({
      ID: Id,

      OBJECTID: ObjectId,

      OBJECTGUID: ObjectUuid,

      CHANGEID: ChangeId,

      HOUSENUM: z.string().optional().describe('Main house number'),

      ADDNUM1: z.string().optional().describe('Additional house number 1'),

      ADDNUM2: z.string().optional().describe('Additional house number 2'),

      HOUSETYPE: z.coerce
        .number<string>()
        .int()
        .describe('Main house type')
        .optional(),

      ADDTYPE1: z.coerce
        .number<string>()
        .int()
        .describe('Additional house type 1')
        .optional(),

      ADDTYPE2: z.coerce
        .number<string>()
        .int()
        .describe('Additional house type 2')
        .optional(),

      OPERTYPEID: OperationTypeId,

      PREVID: PrevId.optional(),

      NEXTID: NextId.optional(),

      UPDATEDATE: UpdateDate,

      STARTDATE: StartDate,

      ENDDATE: EndDate.optional(),

      ISACTUAL: IsActual,

      ISACTIVE: IsActive,
    })
    .transform<House.Data>(attributes => ({
      id: attributes.ID,
      objectId: attributes.OBJECTID,
      objectGuid: attributes.OBJECTGUID,
      changeId: attributes.CHANGEID,
      houseNum: attributes.HOUSENUM,
      addNum1: attributes.ADDNUM1,
      addNum2: attributes.ADDNUM2,
      houseType: attributes.HOUSETYPE,
      addType1: attributes.ADDTYPE1,
      addType2: attributes.ADDTYPE2,
      operationTypeId: attributes.OPERTYPEID,
      prevId: attributes.PREVID,
      nextId: attributes.NEXTID,
      updateDate: attributes.UPDATEDATE,
      startDate: attributes.STARTDATE,
      endDate: attributes.ENDDATE,
      isActual: attributes.ISACTUAL,
      isActive: attributes.ISACTIVE,
    }))

  readonly type = 'House'

  protected get schema() {
    return House.SCHEMA
  }
}
